<template>
    <EntityDetailsPropertiesTabCard
        class="EntityDetailsPropertiesTabCustomPropertiesCard"
        title="Custom Properties"
        :editing="isEditing"
    >
        <transition name="EntityDetailsPropertiesTab__modeFade" mode="out-in">
            <!-- READ MODE -->
            <div v-if="!isEditing" key="read" class="EntityDetailsPropertiesTab__readView">
                <div class="EntityDetailsPropertiesTab__readGrid">
                    <template v-for="field in customPropertyReadFields" :key="field.key">
                        <span
                            class="EntityDetailsPropertiesTab__readLabel"
                        >{{ field.label }}</span>
                        <div>
                            <!-- Address-type custom property -->
                            <address
                                v-if="isAddressField(field.schema)"
                                class="EntityDetailsPropertiesTab__address"
                            >
                                <template v-if="getInsuredValue(field.key)">
                                    <span
                                        v-for="(line, i) in getAddressLines(field.key)"
                                        :key="i"
                                    >{{ line }}</span>
                                </template>
                                <span v-else class="EntityDetailsPropertiesTab__readEmpty">—</span>
                            </address>
                            <!-- Array-type custom property -->
                            <template v-else-if="isArrayValue(field.key)">
                                <div
                                    v-if="filteredArrayValue(field.key, field.schema).length"
                                    class="EntityDetailsPropertiesTab__readValue"
                                >
                                    <div
                                        v-for="(item, i) in filteredArrayValue(field.key, field.schema)"
                                        :key="i"
                                    >
                                        {{ item }}
                                    </div>
                                </div>
                                <span v-else class="EntityDetailsPropertiesTab__readValue EntityDetailsPropertiesTab__readEmpty">—</span>
                            </template>
                            <!-- All other types -->
                            <span
                                v-else
                                class="EntityDetailsPropertiesTab__readValue"
                                :class="{ 'EntityDetailsPropertiesTab__readEmpty': !hasInsuredValue(field.key) }"
                            >{{ formatCustomValue(field.key, field.schema) }}</span>
                        </div>
                    </template>
                </div>
            </div>

            <!-- EDIT MODE -->
            <div v-else key="edit" class="EntityDetailsPropertiesTab__form">
                <JsonSchemaCustomFormElement
                    wrap-value-mode
                    :touched="saveTouched"
                    :no-local-touch="!saveTouched"
                    :field="insuredFieldsField"
                    :depth="0"
                    :custom-component-input="getCustomComponent"
                    :disabled="!entity.active"
                    :rbac-disabled="isDisabledByRbac"
                    :strings="jsonSchemaElementStrings"
                    @change="$emit('custom-properties-change')"
                />
            </div>
        </transition>
    </EntityDetailsPropertiesTabCard>
</template>

<style lang="scss">
.EntityDetailsPropertiesTabCustomPropertiesCard {
    min-width: 0;
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { JsonSchemaCustomFormElement, JsonSchemaFormStrings } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import JsonSchemaCountryEnumForm from '@evidentid/dashboard-commons/components/JsonSchemaForm/customElements/JsonSchemaCountryEnumForm.vue';
    import { Pointer } from '@evidentid/json-schema/FormController';
    import { JsonFormType } from '@evidentid/json-schema/interfaces/JsonForm';
    import { TprmSchemaDisplayFormat } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';

    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import CriterionEnumInput from '@/modules/decisioning-criteria/components/CriterionInputComponents/CriterionEnumInput.vue';
    import EntityDetailsAddressForm from '@/modules/entity-details/components/CustomJsonForms/EntityDetailsAddressForm/EntityDetailsAddressForm.vue';
    import EntityDetailsBooleanForm from '@/modules/entity-details/components/CustomJsonForms/EntityDetailsBooleanForm.vue';
    import EntityDetailsStringForm from '@/modules/entity-details/components/CustomJsonForms/EntityDetailsStringForm.vue';
    import { getJsonSchemaCompFunction } from '@/utils/getJsonSchemaCustomCompFuntion';

    import {
        camelToLabel,
        filteredArrayValue,
        formatCustomValue,
        getAddressLines,
        getInsuredValue,
        hasInsuredValue,
        isAddressField,
        isArrayValue,
    } from '../helpers';

    import EntityDetailsPropertiesTabCard from './EntityDetailsPropertiesTabCard.vue';

    export default defineComponent({
        name: 'EntityDetailsPropertiesTabCustomPropertiesCard',
        components: {
            JsonSchemaCustomFormElement,
            EntityDetailsPropertiesTabCard,
        },
        props: {
            entity: {
                type: Object as PropType<Entity>,
                required: true,
            },
            insuredFieldsField: {
                type: Object as PropType<Pointer>,
                required: true,
            },
            isEditing: {
                type: Boolean,
                required: true,
            },
            isDisabledByRbac: {
                type: Boolean,
                required: true,
            },
            saveTouched: {
                type: Boolean,
                required: true,
            },
            enumLabels: {
                type: Object as PropType<CategorizedEnumLabels>,
                default: (): CategorizedEnumLabels => ({}) as CategorizedEnumLabels,
            },
        },
        emits: [ 'custom-properties-change' ],
        data() {
            const customComponents = {
                [JsonFormType.boolean]: EntityDetailsBooleanForm,
                [JsonFormType.string]: EntityDetailsStringForm,
                [JsonFormType.enum]: CriterionEnumInput,
                [TprmSchemaDisplayFormat.address]: EntityDetailsAddressForm,
                [TprmSchemaDisplayFormat.country]: JsonSchemaCountryEnumForm,
            };
            return {
                getCustomComponent: getJsonSchemaCompFunction(customComponents),
            };
        },
        computed: {
            jsonSchemaElementStrings(): Partial<JsonSchemaFormStrings> {
                return { customLabels: { countryLabels: this.enumLabels.countryCode }, requiredLabel: '*' };
            },
            customPropertyReadFields(): { key: string, label: string, schema: any }[] {
                const field = this.insuredFieldsField as any;
                if (!field?.properties) {
                    return [];
                }
                return Object.entries(field.properties as Record<string, any>)
                    .map(([ key, ptr ]) => ({
                        key,
                        label: (ptr as any).form?.schema?.title || (ptr as any).schema?.title || camelToLabel(key),
                        schema: (ptr as any).form?.schema || (ptr as any).schema || {},
                    }))
                    // The entity form's schema leaves Equipment out, as it does Collaterals, so an
                    // Equipment property does not normally reach this card at all. This guards the
                    // list against one that does: Equipment is shown in a section of its own on the
                    // tab, and listing it here as well would show it twice.
                    .filter(({ schema }) => schema.displayFormat !== TprmSchemaDisplayFormat.equipment);
            },
        },
        methods: {
            getInsuredValue(key: string): any {
                return getInsuredValue(this.entity, key);
            },
            hasInsuredValue(key: string): boolean {
                return hasInsuredValue(this.entity, key);
            },
            isAddressField(schema: any): boolean {
                return isAddressField(schema);
            },
            isArrayValue(key: string): boolean {
                return isArrayValue(this.entity, key);
            },
            getAddressLines(key: string): string[] {
                return getAddressLines(this.entity, key, this.enumLabels);
            },
            filteredArrayValue(key: string, schema?: any): string[] {
                return filteredArrayValue(this.entity, key, schema);
            },
            formatCustomValue(key: string, schema: any): string {
                return formatCustomValue(this.entity, key, schema, this.enumLabels);
            },
        },
    });
</script>
